import HelpIcon from "@mui/icons-material/HelpOutlineRounded";
import LogoutIcon from "@mui/icons-material/LogoutRounded";
import ManageAccountsIcon from "@mui/icons-material/ManageAccountsOutlined";
import VideoIcon from "@mui/icons-material/OndemandVideoRounded";
import {
  Divider,
  FormControlLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router";
import MultilineTooltipTitle from "@entrilia/shared/components/MultilineTooltipTitle";
import UserAvatar from "@entrilia/shared/components/UserAvatar";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import useClientContext from "../../context/useClientContext";
import useUserContext from "../../context/useUserContext";
import { pageRoutes } from "../../routes";
import { getOrganizationClientCode } from "../../utilities/clientHelper.ts";
import { getSharedVariables } from "@entrilia/shared/variables";
import MenuLinkItem from "../common/MenuLinkItem";
import OpenExternalLinkMenuItem from "../common/OpenExternalLinkMenuItem";
import UserInfoMenuItem from "../common/UserInfoMenuItem";
import AccountPopupMenu from "./AccountPopupMenu";
import ColorSchemeMenu from "./ColorSchemeMenu";

const BottomMenu = () => {
  const { name, email, avatarImage, isBetaAccessAvailable, isBetaAccessEnabled, updateBetaAccessEnabled, clients } =
    useUserContext();

  const { clientCode, hasAnyPermission, isFundAdmin, settings } = useClientContext();

  const orgClientCode = useMemo(() => getOrganizationClientCode(clientCode, clients), [clientCode, clients]);

  const hasAccessToVideoLibrary = hasAnyPermission(["ViewOrganizationVideoLibrary", "ManageOrganizationVideoLibrary"]);

  const betaFeaturesTooltipLines = [
    "Current Beta Features:",
    "",
    ...(settings.betaOnlyFeatures.length > 0
      ? settings.betaOnlyFeatures.map((f) => `•︎  ${autoFormatCamelCase(f)}`).sort()
      : ["None"]),
  ];

  return (
    <MenuList>
      {isBetaAccessAvailable && (
        <Tooltip
          arrow
          enterDelay={500}
          enterNextDelay={500}
          title={<MultilineTooltipTitle lines={betaFeaturesTooltipLines} />}
        >
          <MenuItem>
            <FormControlLabel
              control={
                <Switch
                  checked={isBetaAccessEnabled}
                  onChange={(_, checked) => updateBetaAccessEnabled(checked)}
                  sx={{ mr: -1 }}
                />
              }
              label={<Typography>Beta Features</Typography>}
              labelPlacement="start"
              sx={{
                justifyContent: "space-between",
                width: "100%",
                ml: 0,
              }}
            />
          </MenuItem>
        </Tooltip>
      )}

      {hasAccessToVideoLibrary && isFundAdmin && (
        <MenuLinkItem title="Video Library" href={`/${clientCode}/${pageRoutes.videoLibrary}`} Icon={VideoIcon} />
      )}

      {hasAccessToVideoLibrary && !isFundAdmin && Boolean(orgClientCode) && (
        <OpenExternalLinkMenuItem
          title="Video Library"
          href={`${window.location.origin}/${orgClientCode}/${pageRoutes.videoLibrary}`}
          Icon={VideoIcon}
        />
      )}

      <OpenExternalLinkMenuItem title="Help Center" href={getSharedVariables().HELP_CENTER_URI} Icon={HelpIcon} />

      <Divider sx={{ mx: -1 }} />

      <AccountPopupMenu
        renderElement={
          <MenuItem>
            <ListItemIcon>
              <UserAvatar size="small" userName={name} avatarImage={avatarImage} />
            </ListItemIcon>
            <ListItemText>{name}</ListItemText>
          </MenuItem>
        }
      >
        <UserInfoMenuItem userName={name} email={email} avatarImage={avatarImage} />
        <Divider sx={{ my: 1 }} />
        <MenuItem component={RouterLink} to={`/${clientCode}/${pageRoutes.accountSettings}`}>
          <ListItemIcon>
            <ManageAccountsIcon />
          </ListItemIcon>
          <ListItemText primary="Account Settings" />
        </MenuItem>
        {isBetaAccessEnabled && [
          <Divider key="color-scheme-divider" sx={{ my: 1 }} />,
          <ColorSchemeMenu key="color-scheme-menu" />,
        ]}
        <Divider sx={{ my: 1 }} />
        <MenuLinkItem title="Log out" href={`/${clientCode}/logout`} Icon={LogoutIcon} />
      </AccountPopupMenu>
    </MenuList>
  );
};

export default BottomMenu;
